import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import {
  ContractVersionsPageDocument,
  ContractsCustomFieldsPoliciesFragment,
  ContractsEmployeesContractVersionFragment,
  ContractsEmployeesJobCatalogContextlessPoliciesFragment,
  EmployeeProfilePageDocument,
  JobCatalogTreeFragment,
  JobPositionLevelDocument,
  JobPositionRoleDocument,
} from 'generated/resources/graphql'

import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useFixFlowProvider } from 'modules/contracts/components/FixFlow/FixFlowProvider'
import Dates from 'modules/contracts/components/FlowPage/Steps/Dates'
import LegalAndExtras from 'modules/contracts/components/FlowPage/Steps/LegalAndExtras'
import RoleLevelSalary from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary'
import WorkHours from 'modules/contracts/components/FlowPage/Steps/WorkHours'
import { FlowPageContent } from 'modules/contracts/components/FlowPage/_FlowPageContent'
import ContractFlow from 'modules/contracts/lib/ContractFlow'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'

import Footer from './_Footer'
import DatesStep from './lib/DatesStep'
import LegalAndExtrasStep from './lib/LegalAndExtrasStep'
import RoleLevelSalaryStep from './lib/RoleLevelSalaryStep'
import WorkHoursStep from './lib/WorkHoursStep'
import { translations as datesStepTranslations } from './lib/datesTranslations'
import { translations as legalAndExtrasTranslations } from './lib/legalAndExtrasTranslations'
import { translations as roleLevelSalaryTranslations } from './lib/roleLevelSalaryTranslations'
import { translations as templateTranslations } from './lib/templateTranslations'
import { translations as workingHoursTranslations } from './lib/workingHoursTranslations'

// Extended type that includes the contract field from the GraphQL query
interface ContractVersionWithContract extends ContractsEmployeesContractVersionFragment {
  contract?: {
    isDiscontinuous: boolean
  } | null
}

interface Props {
  contractVersion: ContractVersionWithContract
  contractVersions: ContractsEmployeesContractVersionFragment[]
  jobCatalogTrees?: JobCatalogTreeFragment[]
  jobCatalogTreeFeatureEnabled?: boolean
  hasDevTimeTrackingWorkableDaysDefinition?: boolean
  hasTimeTrackingAnnualisationDailyValue?: boolean
  hasPayrollBundle: boolean
  jobCatalogContextlessPolicies: ContractsEmployeesJobCatalogContextlessPoliciesFragment | undefined
  customFieldsContextlessPolicies: ContractsCustomFieldsPoliciesFragment | undefined
}

const FixFlowContent = ({
  contractVersion,
  contractVersions,
  jobCatalogTrees,
  jobCatalogTreeFeatureEnabled,
  hasDevTimeTrackingWorkableDaysDefinition,
  hasTimeTrackingAnnualisationDailyValue,
  hasPayrollBundle,
  jobCatalogContextlessPolicies,
  customFieldsContextlessPolicies,
}: Props) => {
  const { onSubmit: onFixContract, form, isSubmitting } = useFixFlowProvider()
  const apolloClient = useApolloClient()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { legalEntityContractData } = useContractFlow()
  const canSeeWorkingHours = contractVersion._policies?.seeWorkingHours.can ?? false
  const canSeeContractType = contractVersion._policies?.seeContractType.can ?? false
  const canSeeTrialPeriod = contractVersion._policies?.seeTrialPeriod.can ?? false
  const canSeeTrialEndDate = contractVersion._policies?.seeTrialPeriodEndDate.can ?? false
  const canSeeWorkingDayType = contractVersion._policies?.seeWorkingDayType.can ?? false
  const isFixedDiscontinuousContract = contractVersion?.contract?.isDiscontinuous ?? false

  const onClose = () => {
    if (contractVersion?.employee && contractVersion.id) {
      navigate(
        navigation.employees.id.contractVersions.contractVersionId.fullPath({
          id: contractVersion.employee.id.toString(),
          contractVersionId: contractVersion.id.toString(),
        })
      )
    }
  }

  const [isCurrentStepCompleted, setIsCurrentStepCompleted] = React.useState(false)

  const flowSteps = [
    new DatesStep(
      <Dates
        step={1}
        type="fix"
        form={form}
        translations={datesStepTranslations()}
        setIsCurrentStepCompleted={setIsCurrentStepCompleted}
        contractVersionId={contractVersion?.id?.toString()}
        contractVersions={contractVersions}
        isFixedDiscontinuousContract={isFixedDiscontinuousContract}
      />
    ),
  ]

  if (contractVersion.employee) {
    flowSteps.push(
      new RoleLevelSalaryStep(
        <RoleLevelSalary
          step={2}
          type="fix"
          form={form}
          translations={roleLevelSalaryTranslations()}
          setIsCurrentStepCompleted={setIsCurrentStepCompleted}
          jobCatalogTrees={jobCatalogTrees}
          jobCatalogTreeFeatureEnabled={jobCatalogTreeFeatureEnabled}
          canSeeJobTitle={contractVersion?._policies?.seeJobTitle.can ?? false}
          canSeeGrossSalary={contractVersion?._policies?.seeGrossSalary.can ?? false}
          canSeeCompensations={contractVersion?._policies?.seeAdditionalCompensation.can ?? false}
          canSeePayrollPolicy={contractVersion?._policies?.seePayrollPolicy.can ?? false}
          employee={contractVersion.employee}
          contractVersion={contractVersion}
          legalEntityId={contractVersion.employee?.legalEntity?.id ?? '0'}
          hasPayrollBundle={hasPayrollBundle}
          jobCatalogContextlessPolicies={jobCatalogContextlessPolicies}
        />
      )
    )
  }

  // Avoid showing a blank step when hours permissions are disabled
  if (canSeeWorkingHours) {
    flowSteps.push(
      new WorkHoursStep(
        <WorkHours
          step={3}
          type="fix"
          form={form}
          translations={workingHoursTranslations()}
          setIsCurrentStepCompleted={setIsCurrentStepCompleted}
          legalEntityCountry={contractVersion.employee?.legalEntity?.country ?? ''}
          canSeeWorkingHours={canSeeWorkingHours}
          canSeeContractType={canSeeContractType}
          contractVersion={contractVersion}
          hasContractVersions={contractVersions.length > 1}
          hasDevTimeTrackingWorkableDaysDefinition={hasDevTimeTrackingWorkableDaysDefinition}
          hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
        />
      )
    )
  }
  flowSteps.push(
    new LegalAndExtrasStep(
      <LegalAndExtras
        step={canSeeWorkingHours ? 4 : 3}
        type="fix"
        form={form}
        translations={legalAndExtrasTranslations()}
        setIsCurrentStepCompleted={setIsCurrentStepCompleted}
        canSeeContractType={canSeeContractType}
        canSeeTrialPeriod={canSeeTrialPeriod}
        canSeeTrialEndDate={canSeeTrialEndDate}
        canSeeWorkingDayType={canSeeWorkingDayType}
        customFieldsContextlessPolicies={customFieldsContextlessPolicies}
        hasDevTimeTrackingWorkableDaysDefinition={hasDevTimeTrackingWorkableDaysDefinition ?? false}
        employee={contractVersion.employee}
        contractVersion={contractVersion}
      />
    )
  )
  const fixContractFlow = useLazyRef(() => new ContractFlow(flowSteps)).current

  const onSubmit = () => {
    onFixContract(() => {
      if (contractVersion?.employee && contractVersion.id) {
        apolloClient.refetchQueries({
          include: [
            EmployeeProfilePageDocument,
            ContractVersionsPageDocument,
            JobPositionLevelDocument,
            JobPositionRoleDocument,
          ],
        })

        navigate(
          navigation.employees.id.contractVersions.contractVersionId.fullPath({
            id: contractVersion.employee.id.toString(),
            contractVersionId: contractVersion.id.toString(),
          })
        )
      }
    })
  }

  return (
    contractVersion.employee && (
      <FlowPageContent
        translations={templateTranslations()}
        form={form}
        contractVersion={contractVersion}
        legalEntityContractData={legalEntityContractData}
        title={i18n.t('contract_versions.flows.fix.header.title', {
          employeeFullName: contractVersion.employee?.fullName ?? '',
        })}
        flow={fixContractFlow}
        footerContent={
          <Footer
            flow={fixContractFlow}
            employeeFullName={contractVersion.employee?.fullName ?? ''}
          />
        }
        isCurrentStepCompleted={isCurrentStepCompleted}
        isSubmitting={isSubmitting}
        onClose={onClose}
        onSubmit={() => onSubmit()}
        employee={contractVersion.employee}
      />
    )
  )
}

export default FixFlowContent
